import React from "react";
import { Global, css } from "@emotion/core";
import { useTheme } from "emotion-theming";

export default function GlobalStyle() {
    const theme = useTheme();

    return (
        <Global
            styles={css`
                *,
                *::after,
                *::before {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }
                body {
                    font-size: 16px;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI",
                        Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans",
                        "Helvetica Neue", sans-serif;
                    background-color: ${theme.colors.background};
                    text-rendering: optimizelegibility;
                    -webkit-font-smoothing: antialiased;
                    overflow-y: scroll;
                }
                h1 {
                    font-size: 32px;
                    color: ${theme.colors.title};
                    font-weight: bold;
                    margin-bottom: 24px;
                }
                h2 {
                    font-size: 24px;
                }
                h3 {
                    font-size: 18px;
                }
                h4 {
                    font-size: 16px;
                }
                h2,
                h3,
                h4,
                h5,
                h6 {
                    color: ${theme.colors.title};
                    margin: 24px 0 16px 0;
                    font-weight: bold;
                }
                p {
                    color: ${theme.colors.text};
                    font-size: 16px;
                    line-height: 28px;
                    margin-bottom: 16px;
                    font-weight: 400;
                }
                code.inline-code {
                    display: inline-block;
                    vertical-align: middle;
                    line-height: 1;
                    padding: 0.2em;
                    background-color: #44475a;
                    color: rgba(248, 248, 242);
                    font-size: 14px;
                    border-radius: 3px;
                    font-feature-settings: "clig" 0, "calt" 0;
                    font-variant: no-common-ligatures no-discretionary-ligatures
                        no-historical-ligatures no-contextual;
                }
                h1 code.inline-code,
                h2 code.inline-code {
                    font-size: calc(100% - 5px);
                    padding: 4px;
                }
                a {
                    color: ${theme.colors.text};
                    font-weight: bold;
                    text-decoration: none;
                    &:hover {
                        text-decoration: underline;
                    }
                }
                blockquote {
                    margin-bottom: 16px;
                    width: 100%;
                    p {
                        padding: 1rem;
                        border-radius: 5px;
                        background: ${theme.colors.shape};
                        color: ${theme.colors.text};
                        margin: 0;
                    }
                }
                hr {
                    border: 0;
                    height: 0;
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
                }
                table {
                    border-collapse: separate;
                    border-spacing: 0 4px;
                    margin-top: -4px;
                    margin-bottom: 16px;
                    width: 100%;
                    th,
                    td {
                        margin: 0;
                        color: ${theme.colors.text};
                        background-color: ${theme.colors.shape};
                        border: solid 1px ${theme.colors.shape};
                        border-style: solid none;
                        padding: 12px;
                        :first-of-type {
                            border-left-style: solid;
                            border-top-left-radius: 5px;
                            border-bottom-left-radius: 5px;
                        }
                        :last-child {
                            border-right-style: solid;
                            border-bottom-right-radius: 5px;
                            border-top-right-radius: 5px;
                        }
                    }
                    tr {
                        th {
                            color: ${theme.colors.title};
                            text-align: left;
                            font-weight: bold;
                        }
                    }
                }
                iframe {
                    margin-bottom: 16px;
                }
                img {
                    max-width: 100%;
                }
                ul,
                ol {
                    color: ${theme.colors.text};
                    padding-left: 15px;
                    margin-bottom: 16px;
                    li {
                        line-height: 28px;
                    }
                }
                li ul,
                li ol {
                    margin-bottom: 0;
                }
                .gatsby-highlight {
                    font-family: Hack, SFMono-Regular, Menlo, Monaco, Consolas,
                        "Liberation Mono", "Courier New", monospace;
                    font-variant: no-common-ligatures no-discretionary-ligatures
                        no-historical-ligatures no-contextual;
                    position: relative;
                    z-index: 0;
                    margin: 0 0 16px 0;
                    overflow: auto;
                    .token {
                        font-style: normal !important;
                    }
                }
                pre[class*="language-"] code {
                    font-family: inherit;
                }
                pre[class*="language-"]::before {
                    background: #d9d7e0;
                    border-radius: 0 0 4px 4px;
                    color: #232129;
                    font-size: 12px;
                    font-family: inherit;
                    letter-spacing: 0.075em;
                    line-height: 1;
                    padding: 0.25rem 0.5rem;
                    position: absolute;
                    left: 1rem;
                    text-align: right;
                    text-transform: uppercase;
                    top: 0;
                }
                pre[class~="language-js"]::before,
                pre[class~="language-javascript"]::before {
                    content: "js";
                    background: #f7df1e;
                }
                pre[class~="language-jsx"]::before {
                    content: "jsx";
                    background: #61dafb;
                }
                pre[class~="language-sol"]::before,
                pre[class~="language-solidity"]::before {
                    content: "sol";
                    background: white;
                }
                pre[class~="language-typescript"]::before,
                pre[class~="language-ts"]::before {
                    content: "ts";
                    background: #294e80;
                    color: #fff;
                }
                pre[class~="language-tsx"]::before {
                    content: "tsx";
                    background: #294e80;
                    color: #fff;
                }
                pre[class~="language-graphql"]::before {
                    content: "GraphQL";
                    background: #e10098;
                    color: #fff;
                }
                pre[class~="language-html"]::before {
                    content: "html";
                    background: #005a9c;
                    color: #fff;
                }
                pre[class~="language-css"]::before {
                    content: "css";
                    background: #ff9800;
                    color: #fff;
                }
                pre[class~="language-mdx"]::before {
                    content: "mdx";
                    background: #f9ac00;
                    color: #fff;
                }
                pre[class~="language-shell"]::before {
                    content: "shell";
                }
                pre[class~="language-sh"]::before {
                    content: "sh";
                }
                pre[class~="language-bash"]::before {
                    content: "bash";
                }
                pre[class~="language-yaml"]::before,
                pre[class~="language-yml"]::before {
                    content: "yaml";
                    background: #ffa8df;
                }
                pre[class~="language-markdown"]::before {
                    content: "md";
                }
                pre[class~="language-json"]::before,
                pre[class~="language-json5"]::before {
                    content: "json";
                    background: linen;
                }
                pre[class~="language-diff"]::before {
                    content: "diff";
                    background: #e6ffed;
                }
                pre[class~="language-text"]::before {
                    content: "text";
                    background: #fff;
                }
                pre[class~="language-flow"]::before {
                    content: "flow";
                    background: #e8bd36;
                }
                .prism-code::-webkit-scrollbar-track {
                    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.7);
                    border-radius: 10px;
                    background-color: #282a36;
                }
                .prism-code::-webkit-scrollbar {
                    background-color: #282a36;
                    border-radius: 5px;
                    height: 8px;
                }
                .prism-code::-webkit-scrollbar-thumb {
                    border-radius: 10px;
                    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
                    background-color: #791bff;
                }
                span.o-severity {
                    display: inline-block;
                    position: relative;
                }
                span.o-severity::after {
                    content: " ";
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    position: absolute;
                    top: calc(50% - 4px);
                    left: calc(100% + 8px);
                }
                span.o-major {
                    color: #e60000;
                }
                span.o-major::after {
                    background: #e60000;
                }
                span.o-medium {
                    color: #e67300;
                }
                span.o-medium::after {
                    background: #e67300;
                }
                span.o-minor {
                    color: #0073e6;
                }
                span.o-minor::after {
                    background: #0073e6;
                }
                span.o-informational {
                    color: #00b55a;
                }
                span.o-informational::after {
                    background: #00b55a;
                }
                table tbody tr:first-child td a {
                    white-space: nowrap;
                }
                // #gatsby-focus-wrapper {
                //   background-image: url(data:image/png;base64,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);
                //   background-repeat: no-repeat;
                //   background-attachment: fixed;
                //   background-position: center;
                // }
                table tbody tr td a[href*="github"],
                table tbody tr td a[href*="gitlab"] {
                    position: relative;
                }
                table tbody tr td a[href*="manual-review"],
                table tbody tr td a[href*="static-analysis"],
                table tbody tr td a[href*="code-style"] {
                    white-space: nowrap;
                }
                table tbody tr td:nth-child(2) a[href*="github"]::after,
                table tbody tr td:nth-child(2) a[href*="gitlab"]::after {
                    content: " ";
                    position: absolute;
                    top: calc(50% - 0.75rem);
                    left: calc(100% + 0.5rem);
                    width: 1.5rem;
                    height: 1.5rem;
                    background-size: cover;
                }
                table tbody tr td:nth-child(2) a[href*="github"]::after {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='icon icon-tabler icon-tabler-brand-github' width='44' height='44' viewBox='0 0 24 24' stroke-width='1.5' stroke='%232c3e50' fill='none' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath stroke='none' d='M0 0h24v24H0z' fill='none'/%3E%3Cpath d='M9 19c-4.3 1.4 -4.3 -2.5 -6 -3m12 5v-3.5c0 -1 .1 -1.4 -.5 -2c2.8 -.3 5.5 -1.4 5.5 -6a4.6 4.6 0 0 0 -1.3 -3.2a4.2 4.2 0 0 0 -.1 -3.2s-1.1 -.3 -3.5 1.3a12.3 12.3 0 0 0 -6.2 0c-2.4 -1.6 -3.5 -1.3 -3.5 -1.3a4.2 4.2 0 0 0 -.1 3.2a4.6 4.6 0 0 0 -1.3 3.2c0 4.6 2.7 5.7 5.5 6c-.6 .6 -.6 1.2 -.5 2v3.5' /%3E%3C/svg%3E");
                }
                table tbody tr td:nth-child(2) a[href*="gitlab"]::after {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='icon icon-tabler icon-tabler-brand-gitlab' width='44' height='44' viewBox='0 0 24 24' stroke-width='1.5' stroke='%232c3e50' fill='none' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath stroke='none' d='M0 0h24v24H0z' fill='none'/%3E%3Cpath d='M21 14l-9 7l-9 -7l3 -11l3 7h6l3 -7z' /%3E%3C/svg%3E");
                }
                table thead tr th[align="center"] {
                    text-align: center;
                }
                table thead tr th[align="right"] {
                    text-align: right;
                }
                button.issue {
                    background: #8257e6;
                    color: white;
                    padding: 0.5rem 1rem;
                    border-radius: 5px;
                    border: none;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI",
                        Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans",
                        "Helvetica Neue", sans-serif;
                    cursor: pointer;
                    position: relative;
                    transition: background 0.25s cubic-bezier(0.65, 0, 0.076, 1);
                    left: 100%;
                    transform: translateX(-100%);
                    font-weight: 500;
                }
                button.issue:hover {
                    background: #8257e6dd;
                }
                @media print {
                    h2 {
                        break-before: page;
                    }
                }
                .o-severity,
                .o-fixed {
                    max-height: 2rem;
                    vertical-align: middle;
                }
            `}
        />
    );
}
